<script setup>
import { computed, defineEmits, defineProps } from 'vue'
import Select from 'primevue/select'
import MultiSelect from 'primevue/multiselect'
import InputText from 'primevue/inputtext'
import DatePicker from 'primevue/datepicker'
import Button from 'primevue/button'
import ToggleSwitch from 'primevue/toggleswitch'
import FilterBuilderGroup from './FilterBuilderGroup.vue'
import { FIELD_LABELS, emptyCondition, emptyFilterGroup } from '@/utils/dealExportFilters'

const props = defineProps({
  modelValue: { type: Object, required: true },
  statusOptions: { type: Array, default: () => [] },
  groupOptions: { type: Array, default: () => [] },
  offerTagOptions: { type: Array, default: () => [] },
  depth: { type: Number, default: 0 },
  isRoot: { type: Boolean, default: true },
})

const emit = defineEmits(['update:modelValue', 'remove'])

const fieldOptions = computed(() =>
  Object.entries(FIELD_LABELS).map(([value, label]) => ({ value, label }))
)

const opOptions = [
  { label: 'AND', value: 'and' },
  { label: 'OR', value: 'or' },
]

function emitNode(next) {
  emit('update:modelValue', next)
}

function updateChild(index, child) {
  const children = [...(props.modelValue.children || [])]
  children[index] = child
  emitNode({ ...props.modelValue, children })
}

function removeChild(index) {
  const children = [...(props.modelValue.children || [])]
  children.splice(index, 1)
  emitNode({ ...props.modelValue, children })
}

function addCondition() {
  const children = [...(props.modelValue.children || []), emptyCondition('status')]
  emitNode({ ...props.modelValue, children })
}

function addGroup() {
  const children = [...(props.modelValue.children || []), emptyFilterGroup()]
  emitNode({ ...props.modelValue, children })
}

function updateCondition(index, patch) {
  const child = props.modelValue.children[index]
  updateChild(index, { ...child, ...patch })
}

function onFieldChange(index, field) {
  const base = emptyCondition(field)
  updateChild(index, { ...base, negate: props.modelValue.children[index]?.negate || false })
}

const canNest = computed(() => props.depth < 2)
</script>

<template>
  <div class="filter-builder-group" :class="{ root: isRoot }">
    <div class="group-header">
      <Select
        v-if="(modelValue.children || []).length > 1"
        :modelValue="modelValue.op"
        :options="opOptions"
        optionLabel="label"
        optionValue="value"
        class="op-select"
        @update:modelValue="emitNode({ ...modelValue, op: $event })"
      />
      <span v-else class="group-label">{{ isRoot ? 'Условия' : 'Группа' }}</span>
      <div class="group-actions">
        <Button label="+ условие" size="small" text @click="addCondition" />
        <Button v-if="canNest" label="+ группа" size="small" text @click="addGroup" />
        <Button
          v-if="!isRoot"
          icon="pi pi-times"
          size="small"
          text
          severity="danger"
          @click="emit('remove')"
        />
      </div>
    </div>

    <div v-if="!(modelValue.children || []).length" class="empty-hint">
      Нет условий — будут показаны все заказы
    </div>

    <div
      v-for="(child, index) in modelValue.children || []"
      :key="index"
      class="child-row"
    >
      <FilterBuilderGroup
        v-if="child.type === 'group'"
        :modelValue="child"
        :statusOptions="statusOptions"
        :groupOptions="groupOptions"
        :offerTagOptions="offerTagOptions"
        :depth="depth + 1"
        :isRoot="false"
        @update:modelValue="updateChild(index, $event)"
        @remove="removeChild(index)"
      />

      <div v-else class="condition-card">
        <Select
          :modelValue="child.field"
          :options="fieldOptions"
          optionLabel="label"
          optionValue="value"
          placeholder="Поле"
          class="field-select"
          @update:modelValue="onFieldChange(index, $event)"
        />

        <div class="negate-wrap">
          <span>Исключить</span>
          <ToggleSwitch
            :modelValue="child.negate"
            @update:modelValue="updateCondition(index, { negate: $event })"
          />
        </div>

        <MultiSelect
          v-if="child.field === 'status'"
          :modelValue="child.values || []"
          :options="statusOptions"
          placeholder="Статусы"
          display="chip"
          filter
          class="value-control"
          @update:modelValue="updateCondition(index, { values: $event })"
        />
        <MultiSelect
          v-else-if="child.field === 'offer_tag'"
          :modelValue="child.values || []"
          :options="offerTagOptions"
          placeholder="Теги"
          display="chip"
          filter
          class="value-control"
          @update:modelValue="updateCondition(index, { values: $event })"
        />
        <MultiSelect
          v-else-if="child.field === 'group'"
          :modelValue="child.values || []"
          :options="groupOptions"
          placeholder="Группы"
          display="chip"
          filter
          class="value-control"
          @update:modelValue="updateCondition(index, { values: $event })"
        />
        <InputText
          v-else-if="child.field === 'title'"
          :modelValue="child.pattern || ''"
          placeholder="Текст в составе заказа"
          class="value-control"
          @update:modelValue="updateCondition(index, { pattern: $event })"
        />
        <div v-else-if="child.field === 'created_at'" class="date-range">
          <DatePicker
            :modelValue="child.from ? new Date(child.from) : null"
            dateFormat="yy-mm-dd"
            placeholder="От"
            showIcon
            @update:modelValue="updateCondition(index, { from: $event ? $event.toISOString().slice(0, 10) : null })"
          />
          <DatePicker
            :modelValue="child.to ? new Date(child.to) : null"
            dateFormat="yy-mm-dd"
            placeholder="До"
            showIcon
            @update:modelValue="updateCondition(index, { to: $event ? $event.toISOString().slice(0, 10) : null })"
          />
        </div>

        <Button icon="pi pi-trash" text severity="danger" size="small" @click="removeChild(index)" />
      </div>
    </div>
  </div>
</template>

<style scoped>
.filter-builder-group {
  border: 1px solid var(--p-content-border-color, #e2e8f0);
  border-radius: 8px;
  padding: 0.75rem;
  background: #fafafa;
}

.filter-builder-group.root {
  background: #fff;
}

.group-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.5rem;
  margin-bottom: 0.5rem;
}

.group-label {
  font-weight: 600;
  font-size: 0.9rem;
}

.op-select {
  width: 7rem;
}

.group-actions {
  display: flex;
  gap: 0.25rem;
  flex-wrap: wrap;
}

.empty-hint {
  color: #64748b;
  font-size: 0.85rem;
  padding: 0.25rem 0;
}

.child-row + .child-row {
  margin-top: 0.5rem;
}

.condition-card {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem;
  border: 1px dashed #cbd5e1;
  border-radius: 6px;
  background: #fff;
}

.field-select {
  min-width: 11rem;
}

.value-control {
  flex: 1;
  min-width: 12rem;
}

.negate-wrap {
  display: flex;
  align-items: center;
  gap: 0.35rem;
  font-size: 0.85rem;
  white-space: nowrap;
}

.date-range {
  display: flex;
  gap: 0.5rem;
  flex-wrap: wrap;
}
</style>
